import PropTypes from 'prop-types';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, Badge, Avatar } from '@components/ui';
import { PRIORITY_CONFIG, TYPE_CONFIG } from '@config/constants';
import { useThemeStore, useUIStore } from '@store';
import { daysUntil, formatDay } from '@utils/calendarDate';

export default function TaskCard({ task, onClick }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const setSelectedSprintId = useUIStore((state) => state.setSelectedSprintId);
  const setActiveView = useUIStore((state) => state.setActiveView);

  const handleSprintClick = (e) => {
    e.stopPropagation();
    if (task.sprint?.id) {
      setSelectedSprintId(task.sprint.id);
      setActiveView('sprint-detail');
    }
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const priorityConfig = PRIORITY_CONFIG[task.priority?.toLowerCase()] || PRIORITY_CONFIG.medium;
  const typeConfig = TYPE_CONFIG[task.type?.toLowerCase()] || TYPE_CONFIG.task;

  // Check if task is overdue or due soon
  const getDueDateInfo = () => {
    if (!task.dueDate) return null;
    const diffDays = daysUntil(task.dueDate);

    if (diffDays < 0) {
      return {
        label: 'Vencida',
        color: isDark ? '#f87171' : '#ef4444',
        bgColor: isDark ? 'rgba(239, 68, 68, 0.2)' : '#fef2f2',
      };
    } else if (diffDays === 0) {
      return {
        label: 'Hoy',
        color: isDark ? '#fbbf24' : '#f59e0b',
        bgColor: isDark ? 'rgba(245, 158, 11, 0.2)' : '#fffbeb',
      };
    } else if (diffDays <= 2) {
      return {
        label: `${diffDays}d`,
        color: isDark ? '#fbbf24' : '#f59e0b',
        bgColor: isDark ? 'rgba(245, 158, 11, 0.2)' : '#fffbeb',
      };
    }
    return {
      label: formatDay(task.dueDate, { day: 'numeric', month: 'short' }),
      color: isDark ? '#94a3b8' : '#64748b',
      bgColor: isDark ? 'rgba(100, 116, 139, 0.2)' : '#f1f5f9',
    };
  };

  const dueDateInfo = getDueDateInfo();

  const getAssigneeInitials = (assignee) => {
    const name = assignee.user?.name || assignee.name || '';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  const assignees = task.assignees || [];

  // Check if task has blocking relations
  const isBlocked = task.relations?.blockedBy?.length > 0;
  const blockingCount = task.relations?.blocking?.length || 0;

  // Get SLA compliance badge info
  const getSlaComplianceInfo = () => {
    if (!task.slaComplianceLevel || task.status !== 'done') return null;

    const config = {
      on_time: {
        label: 'SLA: A tiempo',
        color: isDark ? '#4ade80' : '#16a34a',
        bgColor: isDark ? 'rgba(34, 197, 94, 0.2)' : '#dcfce7',
      },
      slight_delay: {
        label: 'SLA: Leve retraso',
        color: isDark ? '#fbbf24' : '#d97706',
        bgColor: isDark ? 'rgba(245, 158, 11, 0.2)' : '#fef3c7',
      },
      significant_delay: {
        label: 'SLA: Retraso',
        color: isDark ? '#f87171' : '#dc2626',
        bgColor: isDark ? 'rgba(239, 68, 68, 0.2)' : '#fee2e2',
      },
    };

    return config[task.slaComplianceLevel] || null;
  };

  const slaComplianceInfo = getSlaComplianceInfo();

  // Theme-aware colors
  const titleClass = isDark ? 'text-slate-100' : 'text-slate-800';
  const metaClass = isDark ? 'text-slate-400' : 'text-slate-500';
  const blockedStyle = {
    backgroundColor: isDark ? 'rgba(239, 68, 68, 0.2)' : '#fee2e2',
    color: isDark ? '#f87171' : '#b91c1c',
  };
  const blockingStyle = {
    backgroundColor: isDark ? 'rgba(245, 158, 11, 0.2)' : '#fef3c7',
    color: isDark ? '#fbbf24' : '#b45309',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Card
        className="mb-2.5 cursor-grab active:cursor-grabbing hover:shadow-card-hover transition-all"
        padding="md"
        onClick={() => onClick?.(task)}
      >
        <div className="flex gap-1.5 mb-2 flex-wrap">
          <span className="text-xs">{typeConfig.icon}</span>
          <Badge
            variant={
              task.priority?.toLowerCase() === 'critical'
                ? 'danger'
                : task.priority?.toLowerCase() === 'high'
                  ? 'warning'
                  : 'slate'
            }
            size="sm"
          >
            {priorityConfig.label}
          </Badge>
          {isBlocked && (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded font-medium"
              style={blockedStyle}
              title={`Bloqueada por: ${task.relations.blockedBy.map((r) => r.task.title).join(', ')}`}
            >
              Bloqueada
            </span>
          )}
          {blockingCount > 0 && (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded font-medium"
              style={blockingStyle}
              title={`Bloquea a: ${task.relations.blocking.map((r) => r.task.title).join(', ')}`}
            >
              Bloquea {blockingCount}
            </span>
          )}
          {slaComplianceInfo && (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded font-medium"
              style={{
                backgroundColor: slaComplianceInfo.bgColor,
                color: slaComplianceInfo.color,
              }}
              title={`Nivel de cumplimiento SLA: ${task.slaComplianceLevel}`}
            >
              {slaComplianceInfo.label}
            </span>
          )}
          {task.sprint && (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded font-medium cursor-pointer hover:opacity-80"
              style={{
                backgroundColor: isDark ? 'rgba(99, 102, 241, 0.2)' : '#eef2ff',
                color: isDark ? '#a5b4fc' : '#4f46e5',
              }}
              onClick={handleSprintClick}
              title={`Sprint: ${task.sprint.name}`}
            >
              🏃 {task.sprint.name}
            </span>
          )}
          {task.epic && (
            <span
              className="text-[10px] ml-auto px-1.5 py-0.5 rounded"
              style={{ backgroundColor: task.epic.color + '20', color: task.epic.color }}
            >
              {task.epic.name}
            </span>
          )}
        </div>

        <h4 className={`text-[13px] font-semibold ${titleClass} mb-2.5 leading-snug line-clamp-2`}>
          {task.title}
        </h4>

        <div className="flex justify-between items-center">
          <div className={`flex gap-2 text-[11px] ${metaClass}`}>
            {task.points && <span title="Story Points">🎯 {task.points} pts</span>}
            {task.hours && <span title="Horas estimadas">⏱️ {task.hours}h</span>}
            {dueDateInfo && (
              <span
                title={`Fecha límite: ${formatDay(task.dueDate)}`}
                style={{
                  color: dueDateInfo.color,
                  backgroundColor: dueDateInfo.bgColor,
                  padding: '1px 6px',
                  borderRadius: '4px',
                  fontWeight: '500',
                }}
              >
                📅 {dueDateInfo.label}
              </span>
            )}
          </div>

          {assignees.length > 0 && (
            <div className="flex -space-x-1.5">
              {assignees.slice(0, 3).map((assignee, i) => (
                <Avatar
                  key={assignee.id || i}
                  initials={getAssigneeInitials(assignee)}
                  size="xs"
                />
              ))}
              {assignees.length > 3 && (
                <span className={`text-[10px] ${metaClass} ml-1 self-center`}>
                  +{assignees.length - 3}
                </span>
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

TaskCard.propTypes = {
  task: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    status: PropTypes.string,
    priority: PropTypes.string,
    type: PropTypes.string,
    points: PropTypes.number,
    hours: PropTypes.number,
    dueDate: PropTypes.string,
    slaDeadline: PropTypes.string,
    slaComplianceLevel: PropTypes.oneOf(['on_time', 'slight_delay', 'significant_delay']),
    closedAt: PropTypes.string,
    sprint: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    epic: PropTypes.shape({
      name: PropTypes.string,
      color: PropTypes.string,
    }),
    assignees: PropTypes.array,
    relations: PropTypes.shape({
      blocking: PropTypes.array,
      blockedBy: PropTypes.array,
    }),
  }).isRequired,
  onClick: PropTypes.func,
};
